import { createContext, useState } from "react";
import { ViewStateChangeEvent } from "react-map-gl/maplibre";

/**
 * The map view state shared across all map-based form fields within a single process run form
 * session.
 *
 * The value is `null` until the user first moves any map, at which point subsequent maps
 * mounted in the same session will use this as their `initialViewState` instead of the
 * default project location.
 */
type ProcessRunFormMapViewState = {
  longitude: number;
  latitude: number;
  zoom: number;
};

type ProcessRunFormMapViewStateContextValue = {
  mapViewState: ProcessRunFormMapViewState | null;
  setMapView: (viewStateChangeEvent: ViewStateChangeEvent) => void;
};

export const ProcessRunFormMapViewStateContext =
  createContext<ProcessRunFormMapViewStateContextValue>({
    mapViewState: null,
    setMapView: () => undefined,
  });

export function ProcessRunFormMapViewStateProvider({ children }: { children: React.ReactNode }) {
  const [mapViewState, setMapViewState] = useState<ProcessRunFormMapViewState | null>(null);
  const setMapView = ({ viewState }: ViewStateChangeEvent) =>
    setMapViewState({
      longitude: viewState.longitude,
      latitude: viewState.latitude,
      zoom: viewState.zoom,
    });

  return (
    <ProcessRunFormMapViewStateContext.Provider value={{ mapViewState, setMapView }}>
      {children}
    </ProcessRunFormMapViewStateContext.Provider>
  );
}
